import { useState } from "react";
import { ArrowLeft, Bath, Bed, Building2, Car, Check, MapPin, Phone, Square } from "lucide-react";
import PropertyListingCard from "@/components/PropertyListingCard";
import { useProperty, useProperties } from "@/hooks/useProperties";

type PropertyDetailsPageProps = {
  propertyId?: string;
};

const PropertyDetailsPage = ({ propertyId }: PropertyDetailsPageProps) => {
  const [activeTab, setActiveTab] = useState<"overview" | "features" | "location">("overview");
  const { data: property, isLoading } = useProperty(propertyId);
  const { data: relatedProperties } = useProperties({
    listing_type: property?.listing_type,
    location_id: property?.location_id ?? undefined,
    exclude_id: property?.id,
    limit: 3,
    enabled: Boolean(property),
  });

  if (isLoading) {
    return (
      <main className="pt-32 md:pt-36 px-4 md:px-16 py-16">
        <p className="text-center text-primary-foreground/70 font-body">Loading property...</p>
      </main>
    );
  }

  if (!property) {
    return (
      <main className="pt-32 md:pt-36 px-4 md:px-16 py-16">
        <div className="max-w-3xl mx-auto text-center bg-card border border-border rounded-2xl p-10">
          <h1 className="font-display text-3xl font-light text-foreground mb-4">Property not found</h1>
          <p className="font-body text-muted-foreground mb-6">This property has not been added to the database yet, or it may have been removed.</p>
          <a href="/" className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-lg font-body text-sm hover:bg-accent/90">
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </a>
        </div>
      </main>
    );
  }

  const isSale = property.listing_type === "sale";
  const images = [property.image, ...property.gallery_images.filter((image) => image && image !== property.image)];
  const locationName = property.location_details?.name?.trim() || property.location?.trim() || "Nairobi";
  const locationMapQuery = locationName.toLowerCase().includes("nairobi")
    ? `${locationName}, Kenya`
    : `${locationName}, Nairobi, Kenya`;
  const locationMapUrl = `https://www.google.com/maps?q=${encodeURIComponent(locationMapQuery)}&z=14&output=embed`;
  const locationSummary = property.location_details?.area
    ? `${property.title} is in ${locationName}, ${property.location_details.area}. The map shows the wider ${locationName} area for privacy, not the exact property location.`
    : `${property.title} is in ${locationName}. The map shows the wider ${locationName} area for privacy, not the exact property location.`;

  return (
    <main className="pt-32 md:pt-36 w-full">
      <div className="px-4 md:px-16 py-6">
        <nav className="flex items-center gap-2 text-sm text-primary-foreground/70 flex-wrap">
          <a href="/" className="hover:text-primary-foreground transition-colors">Home</a>
          <span>/</span>
          <a href={isSale ? "/properties-for-sale" : "/properties-for-rent"} className="hover:text-primary-foreground transition-colors">
            Properties for {isSale ? "Sale" : "Rent"}
          </a>
          {property.location_details?.slug && (
            <>
              <span>/</span>
              <a href={`/locations/${property.location_details.slug}`} className="hover:text-primary-foreground transition-colors">{property.location}</a>
            </>
          )}
          <span>/</span>
          <span>{property.title}</span>
        </nav>
      </div>

      <div className="px-4 md:px-16 py-4 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          <div className="lg:w-2/3">
            <div className="relative aspect-[16/10] overflow-hidden rounded-2xl mb-4">
              <img src={images[0]} alt={property.title} className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                <div className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-medium">{property.status_label}</div>
                <div className="px-3 py-1 bg-foreground/90 text-primary-foreground rounded-full text-xs font-medium">{property.category_label || property.category}</div>
              </div>
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {images.slice(1, 5).map((image, index) => (
                  <div key={`${image}-${index}`} className="aspect-[16/10] overflow-hidden rounded-xl">
                    <img src={image} alt={`${property.title} ${index + 2}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:w-1/3">
            <div className="rounded-2xl border border-border/60 bg-card/95 p-6">
              <h1 className="font-display text-2xl md:text-3xl font-light text-foreground mb-4">{property.title}</h1>
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <MapPin className="w-4 h-4" />
                <span className="font-body text-sm">{property.location}</span>
              </div>
              <div className="text-2xl md:text-3xl font-display font-light text-accent mb-6">{property.price}</div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <Stat icon={Bed} label="Bedrooms" value={property.bedrooms} />
                <Stat icon={Bath} label="Bathrooms" value={property.bathrooms} />
                <Stat icon={Square} label="Area" value={property.area} />
                <Stat icon={Car} label="Parking" value={`${property.parking_spaces} Spaces`} />
              </div>

              <div className="space-y-3">
                <a href="/contact" className="block w-full bg-accent text-center text-accent-foreground font-body text-sm tracking-wider uppercase py-3 rounded-lg hover:bg-accent/90 transition-colors">
                  Schedule Viewing
                </a>
                <a href={`https://wa.me/254111653233?text=${encodeURIComponent(`I'm interested in property: ${property.title || 'This Property'} - ${property.location?.trim() || 'Nairobi'}\nView details: ${window.location.href}`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-border rounded-lg py-3 hover:bg-accent/5 transition-colors font-body text-sm">
                  <img src="/whatsapp_icon-.png" alt="WhatsApp" className="w-4 h-4" />
                  WhatsApp Inquiry
                </a>
                <a href="tel:+254111653233" className="flex items-center justify-center gap-2 border border-border rounded-lg py-3 hover:bg-accent/5 transition-colors font-body text-sm">
                  <Phone className="w-4 h-4" />
                  Call Wild Gates
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-16 py-8">
        <div className="mb-8 flex gap-1 overflow-x-auto rounded-lg border border-primary-foreground/10 bg-background/10 p-1 backdrop-blur-sm">
          {[
            ["overview", "Overview"],
            ["features", "Features"],
            ["location", "Location"],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as "overview" | "features" | "location")}
              className={`flex-1 px-4 py-3 font-body text-xs md:text-sm font-medium tracking-wider uppercase transition-all duration-300 rounded-md whitespace-nowrap ${activeTab === id ? "bg-accent text-accent-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-border/60 bg-card/95 p-6 md:p-8">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div>
                <h2 className="font-display text-2xl font-light text-foreground mb-4">Property Description</h2>
                <p className="font-body text-sm md:text-base leading-relaxed text-muted-foreground">{property.description || "Contact Wild Gates for a full brochure and more information about this listing."}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <DetailRow label="Listing Type" value={property.status_label} />
                <DetailRow label="Category" value={property.category_label || property.category} />
                <DetailRow label="Year Built" value={property.year_built || "Not specified"} />
                <DetailRow label="Location" value={property.location} />
              </div>
            </div>
          )}

          {activeTab === "features" && (
            <div>
              <h2 className="font-display text-2xl font-light text-foreground mb-6">Features & Amenities</h2>
              {property.features.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {property.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3 p-4 bg-background rounded-xl border border-border/60">
                      <Check className="w-5 h-5 text-accent" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-body text-muted-foreground">No feature list has been added for this property yet.</p>
              )}
            </div>
          )}

          {activeTab === "location" && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl font-light text-foreground">Location</h2>

              {/* Embedded Map */}
              <div className="bg-background border border-border/60 rounded-2xl p-6 mb-6">
                <div className="aspect-[16/10] overflow-hidden rounded-xl mb-4">
                  <iframe
                    src={locationMapUrl}
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${locationName} area map`}
                  />
                </div>

                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-accent mt-1" />
                  <div>
                    <h3 className="font-body text-base font-medium text-foreground">{locationName}</h3>
                    <p className="font-body text-sm text-muted-foreground mt-2">
                      {locationSummary}
                    </p>
                    <p className="font-body text-xs text-muted-foreground/80 mt-2">
                      Area map only shown for privacy.
                    </p>
                    {property.location_details?.slug && (
                      <a href={`/locations/${property.location_details.slug}`} className="inline-flex items-center gap-2 mt-4 text-accent hover:text-accent/80 font-body text-sm">
                        View all properties in {property.location_details.name}
                        <ArrowLeft className="w-4 h-4 rotate-180" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 md:px-16 py-8 md:py-16">
        <h2 className="font-display text-2xl md:text-3xl font-light text-primary-foreground mb-8">Related Properties</h2>
        {relatedProperties && relatedProperties.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {relatedProperties.map((relatedProperty) => (
              <PropertyListingCard key={relatedProperty.id} {...relatedProperty} statusLabel={relatedProperty.status_label} categoryLabel={relatedProperty.category_label} />
            ))}
          </div>
        ) : (
          <p className="text-primary-foreground/70 font-body">More related properties will appear here as listings are added to the database.</p>
        )}
      </div>
    </main>
  );
};

const Stat = ({ icon: Icon, label, value }: { icon: typeof Bed; label: string; value: string | number }) => (
  <div className="flex items-center gap-3">
    <div className="p-2 bg-accent/20 rounded-lg">
      <Icon className="w-5 h-5 text-accent" />
    </div>
    <div>
      <p className="font-body text-xs text-muted-foreground">{label}</p>
      <p className="font-body text-sm font-medium text-foreground">{value}</p>
    </div>
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between py-3 border-b border-border/30 gap-4">
    <span className="font-body text-sm text-muted-foreground">{label}</span>
    <span className="font-body text-sm text-foreground text-right">{value}</span>
  </div>
);

export default PropertyDetailsPage;
